// src/components/Muestras/Colores.jsx
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import {
  getColores,
  updateColor,
  addColor,
} from "../../api/Muestras/muestraApi";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import GridViewIcon from "@mui/icons-material/GridView";

import ListIcon from "@mui/icons-material/List";

import { Add } from "@mui/icons-material";

import DrawerColores from "./DrawerColores";

const Colores = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);

  const [colores, setColores] = useState([]);
  const [filteredColores, setFilteredColores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("pantone");

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);

  /* Estado para nuevo color y diálogo duplicado  */
  const [newColor, setNewColor] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

  // Estado de vista
  const [viewMode, setViewMode] = useState("grid");

  // 🔹 Convierte HEX → Hue para ordenar por tono
  const hexToHue = (hex) => {
    if (!hex || hex.length < 7) return 0;
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h = 0;
    if (max === min) {
      h = 0;
    } else {
      const d = max - min;
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          break;
      }
      h /= 6;
    }
    return h * 360;
  };

  // 🔹 Función para ordenar según el criterio seleccionado
  const sortColors = (list, criteria) => {
    switch (criteria) {
      case "hex":
        return [...list].sort((a, b) => a.HEX?.localeCompare(b.HEX || ""));
      case "hue":
        return [...list].sort((a, b) => hexToHue(a.HEX) - hexToHue(b.HEX));
      default:
        return [...list].sort((a, b) =>
          a.PANTONE?.localeCompare(b.PANTONE || "")
        );
    }
  };

  // Función para crear nuevo color

  const handleAddNewColor = () => {
    setNewColor({
      COLOR: "",
      HEX: "#FFFFFF",
      RED: 255,
      GREEN: 255,
      BLUE: 255,
      PANTONE: "",
    });
    setSelectedColor({
      COLOR: "",
      HEX: "#FFFFFF",
      RED: 255,
      GREEN: 255,
      BLUE: 255,
      PANTONE: "",
    });
    setOpenDrawer(true);
  };

  const handleColorClick = (color) => {
    setSelectedColor(color);
    setOpenDrawer(true);
  };

  const handleSaveColor = async () => {
    try {
      if (selectedColor.ID_COLOR) {
        // 🟩 Actualización existente
        await updateColor(selectedColor.ID_COLOR, selectedColor);
        const updatedList = colores.map((c) =>
          c.ID_COLOR === selectedColor.ID_COLOR ? selectedColor : c
        );
        setColores(updatedList);
        setFilteredColores(updatedList);
      } else {
        // 🟦 Nuevo color
        try {
          const created = await addColor(selectedColor);
          setColores((prev) => [...prev, created]);
          setFilteredColores((prev) => [...prev, created]);
        } catch (error) {
          if (error.response?.status === 409) {
            // conflicto por HEX duplicado
            setDuplicateDialogOpen(true);
          } else {
            console.error("❌ Error al crear color:", error);
          }
          return; // no cerrar el drawer si hubo error
        }
      }
      setOpenDrawer(false);
    } catch (error) {
      console.error("❌ Error al guardar color:", error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }

      const fetchData = async () => {
        try {
          const data = await getColores();
          const sorted = sortColors(data, sortBy);
          setColores(sorted);
          setFilteredColores(sorted);
        } catch (err) {
          console.error("❌ Error al cargar colores:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    } catch (error) {
      console.error("Error decoding token:", error);
      navigate("/login");
    }
  }, [navigate, sortBy]);

  // recuerda el estado de vista que había elegido
  useEffect(() => {
    const savedView = localStorage.getItem("colorViewMode");
    if (savedView) setViewMode(savedView);
  }, []);

  useEffect(() => {
    localStorage.setItem("colorViewMode", viewMode);
  }, [viewMode]);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    if (!term) return setFilteredColores(colores);
    setFilteredColores(
      colores.filter(
        (c) =>
          c.COLOR?.toLowerCase().includes(term) ||
          c.PANTONE?.toLowerCase().includes(term)
      )
    );
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setFilteredColores(sortColors(colores, e.target.value));
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box
      sx={{
        ...contentMargin,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          position: "sticky",
          top: 16,
          backgroundColor: theme.palette.background.paper,

          zIndex: 10,
          py: 2,
          px: 3,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Pantone Color Library
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,

            flexWrap: "wrap",
          }}
        >
          <TextField
            label="Buscar color o Pantone..."
            variant="outlined"
            size="small"
            onChange={handleSearch}
            sx={{ flex: 1, minWidth: 450 }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Ordenar por</InputLabel>
            <Select
              value={sortBy}
              onChange={handleSortChange}
              label="Ordenar por"
            >
              <MenuItem value="pantone">Pantone (A–Z)</MenuItem>
              <MenuItem value="hex">HEX (código)</MenuItem>
              <MenuItem value="hue">Hue (tono visual)</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Add new Color">
            <IconButton
              onClick={handleAddNewColor}
              sx={{
                border: "1px solid #ffac7e",
                backgroundColor: theme.palette.background.paper,
                color: "#ffac7e",
                "&:hover": { backgroundColor: "#90b2b7", color: "#fff" },
              }}
            >
              <Add />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              viewMode === "grid" ? "Ver como lista" : "Ver como cuadrícula"
            }
          >
            <IconButton
              onClick={() =>
                setViewMode((prev) => (prev === "grid" ? "list" : "grid"))
              }
              sx={{
                border: "1px solid #90b2b7",
                backgroundColor: theme.palette.background.paper,
                color: "#90b2b7",
                "&:hover": { backgroundColor: "#90b2b7", color: "#fff" },
              }}
            >
              {viewMode === "grid" ? <ListIcon /> : <GridViewIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box
        sx={{
          height: "calc(100vh - 80px)",
          overflowY: "auto",
          pr: 1,
        }}
      >
        {viewMode === "grid" ? (
          <Grid container spacing={2}>
            {filteredColores.map((color) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={color.ID_COLOR}>
                <Paper
                  onClick={() => handleColorClick(color)}
                  elevation={2}
                  sx={{
                    cursor: "pointer",
                    p: 1.5,
                    textAlign: "center",
                    borderRadius: 2,
                    transition: "0.2s",
                    "&:hover": { transform: "scale(1.04)" },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: color.HEX,
                      height: 70,
                      borderRadius: 1.5,
                      border: "1px solid rgba(0,0,0,0.1)",
                      mb: 1,
                    }}
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {color.PANTONE || "—"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    {color.COLOR}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "#777",
                      fontFamily: "monospace",
                    }}
                  >
                    {color.HEX}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "#999",
                      fontFamily: "monospace",
                      mt: 0.3,
                    }}
                  >
                    RGB({color.RED}, {color.GREEN}, {color.BLUE})
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Color</TableCell>
                <TableCell>Pantone</TableCell>
                <TableCell>HEX</TableCell>
                <TableCell>RGB</TableCell>
                <TableCell>Preview</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredColores.map((color) => (
                <TableRow
                  key={color.ID_COLOR}
                  hover
                  onClick={() => handleColorClick(color)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{color.COLOR}</TableCell>
                  <TableCell>{color.PANTONE}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {color.HEX}
                  </TableCell>
                  <TableCell>
                    RGB({color.RED}, {color.GREEN}, {color.BLUE})
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        width: 30,
                        height: 20,
                        backgroundColor: color.HEX,
                        borderRadius: 1,
                        border: "1px solid rgba(0,0,0,0.1)",
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <DrawerColores
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        color={selectedColor}
        onChange={setSelectedColor}
        onSave={handleSaveColor}
      />

      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
      >
        <DialogTitle>Color duplicado</DialogTitle>
        <DialogContent>
          <Typography>
            Ya existe un color con este código <strong>HEX</strong>. Por favor
            usa otro valor o revisa la carta de colores.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Colores;
